/**
 * Read the narration windows the orchestrator actually planned (NWP-010).
 *
 * Until the honest-envelope change (orchestrator NWP-005) the delivery envelope
 * fabricated one narration window per scene — `recommended_narration_window.id`
 * was the scene id and `scene_ids` was `[that scene]`. Anything that resolved a
 * window against that shape silently got the wrong answer, while the narration
 * bindings in the same file referenced the real, multi-scene grouping.
 *
 * An envelope now marks each row `windows_source: "planner"` when the grouping
 * is real. Absent that marker we return nothing rather than describing the
 * fabricated shape as if it were a chapter structure.
 */

export type NarrationWindow = {
  id: number;
  sceneIds: string[];
  /** The scene the line is meant to OPEN over — not always the first. */
  anchorSceneId: string | null;
  /** Window runs well past one sentence, so the line covers several beats. */
  oversized: boolean;
  /** Why the window starts here: beat / content / duration / title_card / … */
  boundaryReason: string | null;
  /** Which story beat this window serves, when the generator stamped them. */
  beatIndex: number | null;
  startSecond: number | null;
  endSecond: number | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const numberOrNull = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const sceneIdOrNull = (value: unknown): string | null => {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "string" && value.trim()) return value;
  return null;
};

/**
 * Windows keyed by id, in timeline order. Empty when the envelope carries the
 * legacy fabricated shape, so callers can tell "no chapters" from "one window
 * per scene", which are very different statements.
 */
export function readNarrationWindows(envelope: unknown): NarrationWindow[] {
  if (!isRecord(envelope)) return [];
  const rows = envelope.scene_timeline;
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const byId = new Map<number, NarrationWindow>();
  let sawPlannerRow = false;

  for (const row of rows) {
    if (!isRecord(row)) continue;
    if (row.windows_source !== "planner") continue;
    sawPlannerRow = true;
    const window = row.recommended_narration_window;
    if (!isRecord(window)) continue;
    const id = numberOrNull(window.id);
    if (id === null || byId.has(id)) continue;
    const sceneIds = Array.isArray(window.scene_ids)
      ? window.scene_ids
          .map(sceneIdOrNull)
          .filter((value): value is string => value !== null)
      : [];
    if (sceneIds.length === 0) continue;
    byId.set(id, {
      id,
      sceneIds,
      anchorSceneId: sceneIdOrNull(window.anchor_scene_id),
      oversized: window.oversized === true,
      boundaryReason:
        typeof window.boundary_reason === "string"
          ? window.boundary_reason
          : null,
      beatIndex: numberOrNull(window.beat_index),
      startSecond: numberOrNull(window.start_second),
      endSecond: numberOrNull(window.end_second),
    });
  }

  if (!sawPlannerRow) return [];
  return [...byId.values()].sort((left, right) => left.id - right.id);
}

/** scene id -> the window that contains it. */
export function windowBySceneId(
  windows: NarrationWindow[],
): Map<string, NarrationWindow> {
  const index = new Map<string, NarrationWindow>();
  for (const window of windows) {
    for (const sceneId of window.sceneIds) {
      if (!index.has(sceneId)) index.set(sceneId, window);
    }
  }
  return index;
}

/** Human-readable reason chip, or null when there is nothing worth showing. */
export function boundaryLabel(window: NarrationWindow): string | null {
  switch (window.boundaryReason) {
    case "beat":
      return "story beat";
    case "content":
      return "subject change";
    case "title_card":
      return "title card";
    case "hard_transition":
      return "fade";
    case "duration":
      return "pacing";
    default:
      return null;
  }
}
